import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe, Search, Plus, Edit, Trash2, CheckCircle, AlertCircle,
  Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput,
  Filter, Eye, X, XCircle, CheckSquare, Square, LayoutGrid, Layers, Shield,
  FileText, Link2,
  Clock, FileCode, StickyNote, Tag,
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { useSettings } from '../../core/contexts/SettingsContext';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import arSA from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useTranslation } from 'react-i18next';
import { getBrandLocale, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('ar', arSA);

const EMPTY_FORM = {
  config_number: '',
  provider: '',
  format: '',
  endpoint_url: '',
  api_key_ref: '',
  certificate_expiry: '',
  test_mode: '',
  last_sync_at: '',
  compliance_status: '',
  status: '',
  notes: '',
};

function EInvoicingEditableCell({
  recordId,
  field,
  value,
  className = 'text-white font-semibold',
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  readOnly = true,
  displayValue,
}) {
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false;
            return;
          }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            skipBlurSaveRef.current = true;
            onSave(recordId, field, e.currentTarget.value);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }


  const eInvoiceFormTabs = useMemo(() => ([
    { id: 'connection', label: p('section_connection'), icon: Link2, iconColor: 'text-blue-400' },
    { id: 'compliance', label: p('section_compliance'), icon: Shield, iconColor: 'text-emerald-400' },
  ]), [p]);

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(recordId, field, value);
        }
      }}
      className={`${className} cursor-pointer`}
      title={p('click_to_edit')}
    >
      {value || '-'}
    </span>
  );
}

const formatLabel = (value) => {
  if (!value) return '—';
  if (value === 'UBL') return 'UBL';
  if (value === 'Factur-X') return 'Factur-X';
  if (value === 'JSON') return 'JSON';
  return value;
};

const FacturationElectroniquePage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.facturationElectroniquePage.${key}`, opts);

  const complianceLabel = (value) => {
    if (value === 'conforme') return p('compliance_compliant');
    if (value === 'en_attente') return p('compliance_pending');
    if (value === 'erreur') return p('compliance_error');
    return value || '—';
  };

  const statusLabel = (value) => {
    if (value === 'active') return p('status_active');
    if (value === 'inactive') return p('status_inactive');
    return value || '—';
  };

  const EXPORT_HEADERS = [
    { key: 'config_number', label: p('col_config_number') },
    { key: 'provider', label: p('col_provider') },
    { key: 'format', label: p('col_format') },
    { key: 'endpoint_url', label: p('col_endpoint') },
    { key: 'api_key_ref', label: p('col_api_key_ref') },
    { key: 'certificate_expiry', label: p('col_cert_expiry') },
    { key: 'test_mode', label: p('col_test_mode') },
    { key: 'last_sync_at', label: p('col_last_sync') },
    { key: 'compliance_status', label: p('col_compliance') },
    { key: 'status', label: p('col_status') },
    { key: 'notes', label: p('col_notes') },
  ];
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const { settings } = useSettings();
  const [searchTerm, setSearchTerm] = useState('');
  const [configs, setConfigs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useFacturationModalBodyLock(showModal, showViewModal);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [eInvoiceFormTab, setEInvoiceFormTab] = useState('connection');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_configs');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'إعداد الفوترة الإلكترونية' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'إعداد' });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);

  useEffect(() => { loadConfigs(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadConfigs = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/facturation-electronique');
      const rows = response?.data || response || [];
      setConfigs(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading configs:', error);
      setConfigs([]);
    }
  };

  const stats = useMemo(() => ({
    total: configs.length,
    active: configs.filter((x) => x.status === 'active').length,
    conforme: configs.filter((x) => x.compliance_status === 'conforme').length,
    enAttente: configs.filter((x) => x.compliance_status === 'en_attente').length,
    erreur: configs.filter((x) => x.compliance_status === 'erreur').length,
  }), [configs]);

  const mainTabs = [
    { id: 'all_configs', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_provider', name: p('tab_by_provider'), icon: Globe },
    { id: 'par_statut', name: p('tab_by_status'), icon: Shield },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: CheckCircle },
    { id: 'inactive', name: p('filter_inactive'), icon: XCircle },
    { id: 'conforme', name: p('filter_compliant'), icon: Shield },
  ];

  const configDistribution = {
    all_configs: {
      title: p('dist_overview_title'),
      data: [
        { id: 'active', label: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'conforme', label: p('dist_compliant'), count: stats.conforme, color: 'bg-gradient-to-r from-emerald-500 to-emerald-600' },
        { id: 'en_attente', label: p('dist_pending'), count: stats.enAttente, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { id: 'erreur', label: p('dist_errors'), count: stats.erreur, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_provider: {
      title: p('dist_by_provider_title'),
      data: [...new Set(configs.map((x) => x.provider).filter(Boolean))].slice(0, 4).map((provider) => ({
        id: provider,
        label: provider,
        count: configs.filter((x) => x.provider === provider).length,
        color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      })),
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { id: 'active', label: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { id: 'inactive', label: p('dist_inactive'), count: configs.filter((x) => x.status === 'inactive').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
  };

  const distributionInfo = configDistribution[mainViewMode] || configDistribution.all_configs;

  const columnFilterRules = useMemo(() => [
    textColumnRule('config_number', (row) => row.config_number),
    textColumnRule('provider', (row) => row.provider),
    exactColumnRule('format', (row) => row.format),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const filteredConfigs = useMemo(() => {
    const base = configs.filter((x) => {
      if (statusFilter !== 'all' && x.status !== statusFilter) return false;
      if (dateRange.start || dateRange.end) {
        const createdDate = new Date(x.created_at || x.last_sync_at || 0);
        if (dateRange.start) {
          const startOfDay = new Date(dateRange.start);
          startOfDay.setHours(0, 0, 0, 0);
          if (createdDate < startOfDay) return false;
        }
        if (dateRange.end) {
          const endOfDay = new Date(dateRange.end);
          endOfDay.setHours(23, 59, 59, 999);
          if (createdDate > endOfDay) return false;
        }
      }
      const q = searchTerm.toLowerCase();
      if (q && !(
        x.config_number?.toLowerCase().includes(q) ||
        x.provider?.toLowerCase().includes(q) ||
        x.endpoint_url?.toLowerCase().includes(q)
      )) return false;
      if (mainViewMode === 'all_configs') {
        if (quickFilter === 'active' && x.status !== 'active') return false;
        if (quickFilter === 'inactive' && x.status !== 'inactive') return false;
        if (quickFilter === 'conforme' && x.compliance_status !== 'conforme') return false;
      }
      if (activeDistributionFilter) {
        if (['active', 'inactive'].includes(activeDistributionFilter) && x.status !== activeDistributionFilter) return false;
        if (['conforme', 'en_attente', 'erreur'].includes(activeDistributionFilter) && x.compliance_status !== activeDistributionFilter) return false;
        if (configs.some((c) => c.provider === activeDistributionFilter) && x.provider !== activeDistributionFilter) return false;
      }
      return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [configs, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, filters, columnFilterRules]);

  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredConfigs);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredConfigs.map((r) => r.id), [filteredConfigs]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) setSelectedRecords([]);
    else setSelectedRecords(filteredRecordIds);
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };


  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingRecord(null);
    setShowModal(false);
    setEInvoiceFormTab('connection');
  };

  const openNewConfigModal = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingRecord(null);
    setEInvoiceFormTab('connection');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        test_mode: formData.test_mode === '1' || formData.test_mode === true || formData.test_mode === 1,
      };
      if (editingRecord) {
        await localApiAdapter.put(`/facturation/facturation-electronique/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/facturation/facturation-electronique', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('toast_save_error'));
    } finally {
      resetForm();
      loadConfigs();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      config_number: record.config_number || '',
      provider: record.provider || '',
      format: record.format || '',
      endpoint_url: record.endpoint_url || '',
      api_key_ref: record.api_key_ref || '',
      certificate_expiry: record.certificate_expiry || '',
      test_mode: record.test_mode ? '1' : '',
      last_sync_at: record.last_sync_at || '',
      compliance_status: record.compliance_status || '',
      status: record.status || '',
      notes: record.notes || '',
    });
    setEInvoiceFormTab('connection');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        await localApiAdapter.delete(`/facturation/facturation-electronique/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadConfigs();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('toast_save_error'));
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/facturation/facturation-electronique/${id}`, { status: newStatus });
      loadConfigs();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, value) => {
    const record = configs.find((c) => c.id === recordId);
    if (!record || value === (record[field] || '') || !value.trim()) {
      cancelEditing();
      return;
    }
    try {
      await localApiAdapter.put(`/facturation/facturation-electronique/${recordId}`, { [field]: value });
      await loadConfigs();
      cancelEditing();
      notifyUpdated(showNotify, entityLabel);
    } catch (error) {
      console.error('Error saving inline edit:', error);
      cancelEditing();
      notifyError(showNotify, p('toast_save_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/facturation/facturation-electronique/${id}`)));
        setSelectedRecords([]);
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        loadConfigs();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('toast_save_error'));
      }
    }
  };

  const getExportData = () =>
    selectedRecords.length > 0 ? configs.filter((c) => selectedRecords.includes(c.id)) : filteredConfigs;

  const handleExportCSV = () => {
    exportToCSV(getExportData(), EXPORT_HEADERS, `facturation_electronique_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    await exportToExcel(getExportData(), EXPORT_HEADERS, `facturation_electronique_${new Date().toISOString().split('T')[0]}.xlsx`, p('title'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      await printListPdfWithInvoiceTemplate({
        settings,
        rows: getExportData(),
        columns: [
          { label: p('col_config_number'), value: (c) => c.config_number },
          { label: p('col_provider'), value: (c) => c.provider },
          { label: p('col_format'), value: (c) => formatLabel(c.format) },
          { label: p('col_endpoint'), value: (c) => c.endpoint_url },
          { label: p('col_compliance'), value: (c) => complianceLabel(c.compliance_status) },
          { label: p('col_status'), value: (c) => statusLabel(c.status) },
        ],
        listExportTitle: p('export_list_title'),
        clientTitle: p('export_client_title'),
        printDocumentTitle: `${p('title')} - ${new Date().toISOString().split('T')[0]}`,
      });
      setShowExportDropdown(false);
    } catch (error) {
      console.error('PDF export error:', error);
      alert(p('alert_export_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleImportCSV = () => {
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, EXPORT_HEADERS, { requiredFields: ['provider'] });
        if (result.errors.length > 0) {
          alert(p('alert_import_partial', { valid: result.validCount, invalid: result.invalidCount }));
        } else {
          alert(p('alert_import_success', { count: result.validCount }));
        }
        for (const item of result.data) {
          await localApiAdapter.post('/facturation/facturation-electronique', item);
        }
        loadConfigs();
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv');
  };

  const statusOptions = [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  const complianceOptions = [
    { value: 'conforme', label: p('compliance_compliant'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'en_attente', label: p('compliance_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'erreur', label: p('compliance_error'), icon: AlertCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  const getComplianceBadge = (value) => {
    const config = complianceOptions.find((s) => s.value === value) || complianceOptions[1];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        <Icon size={14} />
        {label}
      </span>
    );
  };

  const TEST_MODE_OPTIONS = [
    { value: '1', label: p('test_mode_yes') },
    { value: '0', label: p('test_mode_no') },
  ];

  const FORMAT_OPTIONS = [
    { value: 'UBL', label: 'UBL' },
    { value: 'Factur-X', label: 'Factur-X' },
    { value: 'JSON', label: 'JSON' },
  ];

  const COMPLIANCE_STATUS_OPTIONS = [
    { value: 'conforme', label: p('compliance_compliant') },
    { value: 'en_attente', label: p('compliance_pending') },
    { value: 'erreur', label: p('compliance_error') },
  ];

  const STATUS_FORM_OPTIONS = [
    { value: 'active', label: p('status_active') },
    { value: 'inactive', label: p('status_inactive') },
  ];

  const listStats = useMemo(() => [
    { key: 'total', title: p('stat_total'), value: stats.total, icon: Globe, color: 'from-blue-500 to-blue-600' },
    { key: 'active', title: p('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { key: 'compliant', title: p('stat_compliant'), value: stats.conforme, icon: Shield, color: 'from-emerald-500 to-emerald-600' },
    { key: 'pending', title: p('stat_pending'), value: stats.enAttente, icon: Clock, color: 'from-yellow-500 to-yellow-600' },
    { key: 'errors', title: p('stat_errors'), value: stats.erreur, icon: AlertCircle, color: 'from-red-500 to-red-600', className: 'max-lg:hidden' },
  ], [stats, p]);
  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('status_active') },
    { value: 'inactive', label: p('status_inactive') },
  ], [p]);
  const qawaemStatusLabel = statusFilter === 'all' ? p('status_filter') : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status_filter'));
  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'config_number', label: p('col_config_number'), required: true },
    { key: 'provider', label: p('col_provider') },
    { key: 'format', label: p('col_format') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 3 }),
    [qawaemBaseColumns, showCol],
  );
  return (
    <div className="space-y-3 employees-container">
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />

<FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-6"
      containerClassName="employees-container"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Globe}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={openNewConfigModal}
      createLabel={p('new_config')}
      headerActions={null}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label') || p('import') || t('common.import'),
        exportLabel: p('export_label') || t('common.export'),
        generatingLabel: p('generating') || t('common.loading'),
        isGeneratingPDF: typeof isGeneratingPDF !== 'undefined' ? isGeneratingPDF : false,
        onImportCSV: typeof handleImportCSV !== 'undefined' ? handleImportCSV : () => setShowImportDropdown(false),
        onImportExcel: typeof handleImportExcel !== 'undefined' ? handleImportExcel : () => setShowImportDropdown(false),
        onImportJSON: typeof handleImportJSON !== 'undefined' ? handleImportJSON : () => {},
        onImportXML: typeof handleImportXML !== 'undefined' ? handleImportXML : () => {},
        onImportTXT: typeof handleImportTXT !== 'undefined' ? handleImportTXT : () => {},
        onExportPDF: typeof handleExportPDF !== 'undefined' ? handleExportPDF : () => {},
        onExportExcel: typeof handleExportExcel !== 'undefined' ? handleExportExcel : () => {},
        onExportCSV: typeof handleExportCSV !== 'undefined' ? handleExportCSV : () => {},
        onExportJSON: typeof handleExportJSON !== 'undefined' ? handleExportJSON : () => {},
        onExportXML: typeof handleExportXML !== 'undefined' ? handleExportXML : () => {},
        onExportHTML: typeof handleExportHTML !== 'undefined' ? handleExportHTML : () => {},
        onExportTXT: typeof handleExportTXT !== 'undefined' ? handleExportTXT : () => {},
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_configs' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} type="button" onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => (
                <button key={item.filterId} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterId ? null : item.filterId)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterId ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterId ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => { const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0); return total > 0 ? (item.count / total * 100).toFixed(1) : 0; })()}%` }} />
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              <FacturationViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} isLight={isDashboardLightMode} />

              <div className="relative" ref={statusFilterRef}>
                <button onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5">
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{statusFilter === 'all' ? p('status_filter') : statusLabel(statusFilter)}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      {['all', 'active', 'inactive'].map((val) => (
                        <button key={val} onClick={() => { setStatusFilter(val); setIsStatusFilterOpen(false); }} className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === val ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}>
                          <span className="text-sm font-medium">{val === 'all' ? p('all_statuses') : statusLabel(val)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={dateRangeRef}>
                <button onClick={() => { setIsDateRangeOpen(!isDateRangeOpen); setShowImportDropdown(false); setShowExportDropdown(false); }} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5">
                  <Calendar size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{p('period')}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-4">
                      <div className="flex flex-col gap-3">
                        <DatePicker selected={dateRange.start} onChange={(date) => setDateRange((prev) => ({ ...prev, start: date }))} selectsStart startDate={dateRange.start} endDate={dateRange.end} locale={getBrandDatePickerLocale()} dateFormat="dd/MM/yyyy" placeholderText={p('start_date')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white" />
                        <DatePicker selected={dateRange.end} onChange={(date) => setDateRange((prev) => ({ ...prev, end: date }))} selectsEnd startDate={dateRange.start} endDate={dateRange.end} minDate={dateRange.start} locale={getBrandDatePickerLocale()} dateFormat="dd/MM/yyyy" placeholderText={p('end_date')} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white" />
                        <div className="flex gap-2 pt-2 border-t border-blue-400/30">
                          <button onClick={() => setDateRange({ start: null, end: null })} className="flex-1 px-3 py-2 bg-white/5 hover:bg-blue-500/20 text-white/80 rounded-xl text-sm">{p('reset')}</button>
                          <button onClick={() => setIsDateRangeOpen(false)} className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl text-sm">{p('apply')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {selectedRecords.length > 0 && (
                <button onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg text-sm">
                  <Trash2 size={16} />
                  <span className="hidden sm:inline">{p('delete_with_count', { count: selectedRecords.length })}</span>
                </button>
              )}

              <div className="relative z-[100001]" ref={importDropdownRef}>
                <button onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg flex items-center gap-1.5">
                  <Upload size={16} />
                  <span className="text-sm font-medium">{p('import')}</span>
                </button>
                {showImportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      <button onClick={handleImportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3">
                        <FileOutput size={16} className="text-purple-400" />
                        <span className="text-sm font-medium">{p('csv')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={exportDropdownRef}>
                <button onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }} className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg flex items-center gap-1.5">
                  <Download size={16} />
                  <span className="text-sm font-medium">{p('export')}</span>
                </button>
                {showExportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3 disabled:opacity-50">
                        <FileText size={16} className="text-red-400" />
                        <span className="text-sm font-medium">{isGeneratingPDF ? p('generating') : p('pdf')}</span>
                      </button>
                      <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3">
                        <FileSpreadsheet size={16} className="text-green-400" />
                        <span className="text-sm font-medium">{p('excel')}</span>
                      </button>
                      <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3">
                        <FileOutput size={16} className="text-purple-400" />
                        <span className="text-sm font-medium">{p('csv')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
{viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('config_number') ? <TableColumnHeaderFilter columnKey="config_number" label={p('col_config_number')} value={filters.config_number || ''} onChange={(v) => setFilter('config_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('provider') ? <TableColumnHeaderFilter columnKey="provider" label={p('col_provider')} value={filters.provider || ''} onChange={(v) => setFilter('provider', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('format') ? <TableColumnHeaderFilter columnKey="format" label={p('col_format')} kind="select" options={FORMAT_OPTIONS} value={filters.format || ''} onChange={(v) => setFilter('format', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_endpoint')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('col_compliance')}</th>
                  {showCol('status') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th> : null}
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_last_sync')}</th>
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredConfigs.length > 0 ? (
                  pagedItems.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      {showCol('config_number') ? <td className="px-6 py-4"><span className="text-white font-semibold">{record.config_number}</span></td> : null}
                      {showCol('provider') ? (
                      <td className="px-6 py-4">
                        <EInvoicingEditableCell recordId={record.id} field="provider" value={record.provider} className="text-white font-semibold" isEditing={editingCell?.recordId === record.id && editingCell?.field === 'provider'} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} />
                      </td>
                      ) : null}
                      {showCol('format') ? <td className="px-6 py-4"><span className="text-white/80">{formatLabel(record.format)}</span></td> : null}
                      <td className="px-6 py-4"><span className="text-white/70 text-sm truncate max-w-[200px] inline-block">{record.endpoint_url || '—'}</span></td>
                      <td className="px-6 py-4 text-center">{getComplianceBadge(record.compliance_status)}</td>
                      {showCol('status') ? <td className="px-6 py-4 text-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></td> : null}
                      <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.last_sync_at ? new Date(record.last_sync_at).toLocaleString(getBrandLocale(i18n.language)) : '—'}</span></td>
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view') || p('view') || t('common.view')}
                          editTitle={p('action_edit') || p('edit') || t('common.edit')}
                          deleteTitle={p('action_delete') || p('delete') || t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleTableColCount} className="text-center py-12 text-white/60">
                      <Globe size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('footer_total', { count: filteredConfigs.length })}</span>
                      <span>{p('active_label')} <span className="text-white font-semibold">{filteredConfigs.filter((c) => c.status === 'active').length}</span></span>
                      <span>{p('compliant_label')} <span className="text-white font-semibold">{filteredConfigs.filter((c) => c.compliance_status === 'conforme').length}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredConfigs.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Globe size={20} className="text-blue-400" />
                          {record.config_number}
                        </h3>
                        {getComplianceBadge(record.compliance_status)}
                        <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-white/70">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_connection')}</h5>
                          <div><span className="font-medium">{p('label_provider_short')}</span> {record.provider || '—'}</div>
                          <div><span className="font-medium">{p('label_endpoint_short')}</span> {record.endpoint_url || '—'}</div>
                          <div><span className="font-medium">{p('label_test_mode_short')}</span> {record.test_mode ? p('yes') : p('no')}</div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_format')}</h5>
                          <div><span className="font-medium">{p('label_format_short')}</span> {formatLabel(record.format)}</div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_compliance')}</h5>
                          <div><span className="font-medium">{p('label_cert_short')}</span> {record.certificate_expiry || '—'}</div>
                          <div><span className="font-medium">{p('label_last_sync_short')}</span> {record.last_sync_at ? new Date(record.last_sync_at).toLocaleString(getBrandLocale(i18n.language)) : '—'}</div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <div>{record.notes || '—'}</div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('view')}
                      editTitle={p('edit')}
                      deleteTitle={p('delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12 text-white/60">
                <Globe size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={viewingRecord.config_number}
          icon={Globe}
          maxWidth="900px"
          testId="e-invoicing-view-modal"
        >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-white/80">
              <div><p className="text-white/50 text-sm">{p('label_provider')}</p><p className="text-white">{viewingRecord.provider || '—'}</p></div>
              <div><p className="text-white/50 text-sm">{p('label_format')}</p><p className="text-white">{formatLabel(viewingRecord.format)}</p></div>
              <div><p className="text-white/50 text-sm">{p('col_endpoint')}</p><p className="text-white">{viewingRecord.endpoint_url || '—'}</p></div>
              <div><p className="text-white/50 text-sm">{p('label_api_key_short')}</p><p className="text-white">{viewingRecord.api_key_ref || '—'}</p></div>
              <div><p className="text-white/50 text-sm">{p('label_cert_expiry')}</p><p className="text-white">{viewingRecord.certificate_expiry || '—'}</p></div>
              <div><p className="text-white/50 text-sm">{p('label_test_mode')}</p><p className="text-white">{viewingRecord.test_mode ? p('yes') : p('no')}</p></div>
              <div><p className="text-white/50 text-sm">{p('label_compliance')}</p><p className="text-white">{complianceLabel(viewingRecord.compliance_status)}</p></div>
              <div><p className="text-white/50 text-sm">{p('label_status')}</p><p className="text-white">{statusLabel(viewingRecord.status)}</p></div>
              <div className="md:col-span-2"><p className="text-white/50 text-sm">{p('section_notes')}</p><p className="text-white">{viewingRecord.notes || '—'}</p></div>
              </div>
        </QawaemErpViewModal>
      ) : null}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('edit_config') : p('new_config')}
          icon={Globe}
          formId="e-invoicing-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          showSaveButton
          maxWidth="900px"
          testId="e-invoicing-crud-modal"
          tabs={eInvoiceFormTabs}
          activeTab={eInvoiceFormTab}
          onTabChange={setEInvoiceFormTab}
        >
              <form id="e-invoicing-form" onSubmit={handleSubmit}>
                {eInvoiceFormTab === 'connection' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_config_number')}</label>
                            <input type="text" value={formData.config_number} onChange={(e) => setFormData({ ...formData, config_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_config_number')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_provider')} required /></label>
                            <input type="text" required value={formData.provider} onChange={(e) => setFormData({ ...formData, provider: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_provider')} />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_endpoint_url')}</label>
                            <input type="url" value={formData.endpoint_url} onChange={(e) => setFormData({ ...formData, endpoint_url: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_endpoint')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_api_key_ref')}</label>
                            <input type="text" value={formData.api_key_ref} onChange={(e) => setFormData({ ...formData, api_key_ref: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_api_key_ref')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_test_mode')}</label>
                            <CustomSelect
                              value={formData.test_mode}
                              onChange={(value) => setFormData({ ...formData, test_mode: value })}
                              options={TEST_MODE_OPTIONS}
                              placeholder={p('placeholder_test_mode')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_format')}</label>
                            <CustomSelect
                              value={formData.format}
                              onChange={(value) => setFormData({ ...formData, format: value })}
                              options={FORMAT_OPTIONS}
                              placeholder={p('placeholder_format')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                        </div>
                )}

                {eInvoiceFormTab === 'compliance' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_cert_expiry')}</label>
                            <CustomDatePicker
                              value={formData.certificate_expiry}
                              onChange={(dateString) => setFormData({ ...formData, certificate_expiry: dateString })}
                              placeholder={p('start_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_compliance_status')}</label>
                            <CustomSelect
                              value={formData.compliance_status}
                              onChange={(value) => setFormData({ ...formData, compliance_status: value })}
                              options={COMPLIANCE_STATUS_OPTIONS}
                              placeholder={p('placeholder_compliance')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_last_sync')}</label>
                            <input type="datetime-local" value={formData.last_sync_at ? formData.last_sync_at.slice(0, 16) : ''} onChange={(e) => setFormData({ ...formData, last_sync_at: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={STATUS_FORM_OPTIONS}
                              placeholder={p('placeholder_status')}
                              searchable={true}
                              creatable={true}
                              autoSelect={false}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                            <textarea rows={4} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_notes')} />
                          </div>
                        </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}
    </div>
  );
};

export default FacturationElectroniquePage;

